import React from 'react';
import { ImageMetadata } from '../../services/imageApi';
import { useAuthenticatedImage } from '../../hooks/useAuthenticatedImage';
import { FileImage, Loader2 } from 'lucide-react';

const PLACEHOLDER_SVG = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23f3f4f6" width="200" height="200"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="14" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3ENo Preview%3C/text%3E%3C/svg%3E';

interface ImageGalleryProps {
  images: ImageMetadata[];
  selectedImage: ImageMetadata | null;
  onSelectImage: (image: ImageMetadata) => void;
}

const GalleryThumbnail: React.FC<{
  image: ImageMetadata;
  isSelected: boolean;
  onClick: () => void;
}> = ({ image, isSelected, onClick }) => {
  const { blobUrl, loading, error, containerRef } = useAuthenticatedImage(image.thumbnailUrl);

  return (
    <div
      ref={containerRef}
      onClick={onClick}
      className={`relative cursor-pointer rounded-lg border-2 transition-all ${
        isSelected
          ? 'border-cyan-600 shadow-lg'
          : 'border-gray-200 dark:border-medical-700 hover:border-gray-300 dark:hover:border-medical-600 hover:shadow-md'
      }`}
    >
      <div className="aspect-square bg-gray-100 dark:bg-medical-900 rounded-t-lg overflow-hidden flex items-center justify-center">
        {loading ? (
          <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
        ) : (
          <img
            src={error ? PLACEHOLDER_SVG : (blobUrl || PLACEHOLDER_SVG)}
            alt={image.filename}
            className="max-w-full max-h-full object-contain"
          />
        )}
      </div>

      <div className="p-2 bg-white dark:bg-medical-800">
        <div className="text-xs font-medium text-gray-900 dark:text-medical-100 truncate" title={image.filename}>
          {image.filename}
        </div>
        {image.pageNumber && (
          <div className="text-xs text-gray-500 dark:text-medical-400 mt-1">
            p.{image.pageNumber}
          </div>
        )}
      </div>

      {isSelected && (
        <div className="absolute top-2 right-2 w-6 h-6 bg-cyan-600 rounded-full flex items-center justify-center">
          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </div>
      )}
    </div>
  );
};

export const ImageGallery: React.FC<ImageGalleryProps> = ({
  images,
  selectedImage,
  onSelectImage,
}) => {
  return (
    <div className="h-full overflow-y-auto bg-white dark:bg-medical-800">
      <div className="p-4 grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {images.map((image) => (
          <GalleryThumbnail
            key={image.id}
            image={image}
            isSelected={selectedImage?.id === image.id}
            onClick={() => onSelectImage(image)}
          />
        ))}
      </div>

      {images.length === 0 && (
        <div className="flex items-center justify-center h-full text-gray-500 dark:text-medical-400">
          <div className="text-center">
            <FileImage className="w-12 h-12 text-gray-300 dark:text-medical-600 mx-auto mb-3" />
            <p>No images to display</p>
          </div>
        </div>
      )}
    </div>
  );
};
